import React, {useRef} from "react";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import {useSettingsRecentRoutes} from "./SettingsRecentRoutes";
import type {
    SettingsSearchFindOption,
    SettingsSearchProps,
    SettingsSearchResult,
} from "./settingsSearch.types";

type RankedSearchResult = {
    result: SettingsSearchResult
    score: number
    index: number
}

type UseSettingsSearchOptions = SettingsSearchProps & {
    autoFocus?: boolean
    idleGoToItemIds?: string[]
    idleCreateItemIds?: string[]
    onClose?: () => void
}

const MIN_PROVIDER_QUERY_LENGTH = 2
const PROVIDER_SEARCH_DEBOUNCE_MS = 200
const CREATE_INTENT_PATTERN = /^(create|add|new)\b/
const EMPTY_FIND_OPTIONS: SettingsSearchFindOption[] = []
const EMPTY_RESULTS: SettingsSearchResult[] = []

const normalize = (value: string) => value.toLowerCase().trim()

const parseInputTrigger = (value: string) => {
    const separatorIndex = value.indexOf(':')
    if (separatorIndex < 0) return null

    const trigger = value.slice(0, separatorIndex).trim()
    if (!trigger) return null

    return {
        trigger,
        term: value.slice(separatorIndex + 1).replace(/^\s+/, ''),
    }
}

const buildCommandResults = (items: SettingsSearchProps['items']): SettingsSearchResult[] => {
    const goToResults = items.map(item => ({
        id: `goTo-${item.id}`,
        section: 'goTo' as const,
        label: item.label,
        group: item.group,
        path: item.path,
        keywords: [...(item.keywords || [])],
    }))

    const createResults = items
        .filter(item => item.createPath && item.createLabel)
        .map(item => ({
            id: `create-${item.id}`,
            section: 'create' as const,
            label: item.createLabel as string,
            group: item.group,
            path: item.createPath as string,
            keywords: [item.label, ...(item.keywords || [])],
        }))

    return [...goToResults, ...createResults]
}

const getResultScore = (
    result: SettingsSearchResult,
    needle: string,
    translate: (value: string) => string,
) => {
    const label = normalize(translate(result.label))
    const labels = result.section === 'create'
        ? Array.from(new Set([
            label,
            `create ${label.replace(CREATE_INTENT_PATTERN, '').trim()}`,
            `add ${label.replace(CREATE_INTENT_PATTERN, '').trim()}`,
            `new ${label.replace(CREATE_INTENT_PATTERN, '').trim()}`,
        ]))
        : [label]
    const path = normalize(result.path || '')
    const keywords = result.keywords.map(keyword => normalize(translate(keyword)))

    if (labels.some(candidateLabel => candidateLabel === needle)) return 0
    if (labels.some(candidateLabel => candidateLabel.startsWith(needle))) return 1
    if (keywords.some(keyword => keyword.startsWith(needle))) return 2
    if (labels.some(candidateLabel => candidateLabel.includes(needle))) return 3
    if (keywords.some(keyword => keyword.includes(needle))) return 4
    if (path.includes(needle)) return 5

    return null
}

const getFindOptionTerms = (
    option: SettingsSearchFindOption,
    translate: (value: string) => string,
) => [
    normalize(option.trigger),
    normalize(translate(option.label)),
    normalize(translate(option.group)),
    ...option.keywords.map(keyword => normalize(translate(keyword))),
].filter(Boolean)

const getFindOptionScore = (
    option: SettingsSearchFindOption,
    needle: string,
    translate: (value: string) => string,
) => {
    const terms = getFindOptionTerms(option, translate)
    const tokens = needle.split(/\s+/).filter(Boolean)

    if (terms.some(term => term === needle)) return 0
    if (terms.some(term => term.startsWith(needle))) return 1
    if (tokens.some(token => terms.some(term => term === token))) return 2
    if (tokens.some(token => terms.some(term => term.startsWith(token)))) return 3
    if (terms.some(term => term.includes(needle))) return 4
    if (tokens.some(token => terms.some(term => term.includes(token)))) return 5

    return null
}

const removeFindOptionTerm = (
    value: string,
    option: SettingsSearchFindOption,
    translate: (value: string) => string,
) => {
    const terms = getFindOptionTerms(option, translate).sort((left, right) => right.length - left.length)

    for (const term of terms) {
        const escapedTerm = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
        const nextValue = value.replace(new RegExp(`\\b${escapedTerm}\\b`, 'i'), '').replace(/\s+/g, ' ').trim()
        if (nextValue !== value.trim()) return nextValue
    }

    const removableToken = value
        .trim()
        .split(/\s+/)
        .find(token => terms.some(term => term === normalize(token) || term.startsWith(normalize(token))))

    if (!removableToken) return value.trim()

    const escapedToken = removableToken.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    return value.replace(new RegExp(`\\b${escapedToken}\\b`, 'i'), '').replace(/\s+/g, ' ').trim()
}

export const useRecentSettingsSearchResults = (items: SettingsSearchProps['items']) => {
    const {recentRouteIds, removeRecentRoute} = useSettingsRecentRoutes()

    return React.useMemo<SettingsSearchResult[]>(() => recentRouteIds.flatMap<SettingsSearchResult>(routeId => {
        const item = items.find(candidate => candidate.id === routeId)
        if (!item) return []

        return [{
            id: `goTo-${item.id}`,
            section: 'goTo',
            label: item.label,
            group: item.group,
            path: item.path,
            keywords: [...(item.keywords || [])],
            onRemove: () => removeRecentRoute(routeId),
        }]
    }), [items, recentRouteIds, removeRecentRoute])
}

export const useSettingsSearch = ({
    items,
    additionalResults = EMPTY_RESULTS,
    searchProvider,
    findOptions = EMPTY_FIND_OPTIONS,
    autoFocus = false,
    idleGoToItemIds,
    idleCreateItemIds,
    onClose,
}: UseSettingsSearchOptions) => {
    const {t} = useTranslation()
    const navigate = useNavigate()
    const recentResults = useRecentSettingsSearchResults(items)
    const [query, setQuery] = React.useState('')
    const [activeIndex, setActiveIndex] = React.useState<number | null>(null)
    const [providerResults, setProviderResults] = React.useState<SettingsSearchResult[]>([])
    const [providersLoading, setProvidersLoading] = React.useState(false)
    const [executingResultId, setExecutingResultId] = React.useState<string | null>(null)
    const [actionError, setActionError] = React.useState(false)
    const inputRef = useRef<HTMLInputElement>(null)
    const executingResultIdRef = useRef<string | null>(null)
    const resultRefs = useRef<Record<string, HTMLDivElement | null>>({})
    const instanceId = React.useId().replace(/:/g, '')
    const resultListboxId = `settings-search-results-${instanceId}`
    const getResultDomId = React.useCallback(
        (resultId: string) => `settings-search-result-${instanceId}-${resultId}`,
        [instanceId],
    )
    const normalizedQuery = React.useMemo(() => normalize(query), [query])
    const inputTrigger = React.useMemo(() => parseInputTrigger(query), [query])
    const searchInputValue = inputTrigger ? inputTrigger.term : query
    const hasSearchInputValue = Boolean(searchInputValue.trim())
    const normalizedSearchInputValue = React.useMemo(() => normalize(searchInputValue), [searchInputValue])
    const hasCreateIntent = React.useMemo(
        () => CREATE_INTENT_PATTERN.test(normalizedSearchInputValue),
        [normalizedSearchInputValue],
    )
    const activeFindOption = React.useMemo(() => {
        if (!inputTrigger) return null

        const trigger = normalize(inputTrigger.trigger)
        return findOptions.find(option => (
            [option.trigger, ...option.keywords].some(keyword => normalize(keyword) === trigger)
        )) || null
    }, [findOptions, inputTrigger])
    const activeSearchScopeLabel = activeFindOption?.label || inputTrigger?.trigger || ''
    const staticResults = React.useMemo(
        () => [...buildCommandResults(items), ...additionalResults],
        [additionalResults, items],
    )
    const rankedStaticResults = React.useMemo(() => {
        if (!normalizedQuery) return staticResults

        return staticResults
            .reduce<RankedSearchResult[]>((rankedResults, result, index) => {
                const score = getResultScore(result, normalizedQuery, t)
                if (score !== null) rankedResults.push({result, score, index})
                return rankedResults
            }, [])
            .sort((left, right) => left.score - right.score || left.index - right.index)
            .map(({result}) => result)
    }, [normalizedQuery, staticResults, t])
    const suggestedFindResults = React.useMemo(() => {
        if (inputTrigger || hasCreateIntent || normalizedSearchInputValue.length < MIN_PROVIDER_QUERY_LENGTH) return []

        const term = searchInputValue.trim()
        return findOptions
            .reduce<Array<{option: SettingsSearchFindOption; score: number; index: number}>>((rankedOptions, option, index) => {
                const score = getFindOptionScore(option, normalizedSearchInputValue, t)
                if (score !== null) rankedOptions.push({option, score, index})
                return rankedOptions
            }, [])
            .sort((left, right) => left.score - right.score || left.index - right.index)
            .slice(0, 4)
            .map<SettingsSearchResult>(({option}) => ({
                id: `find-${option.id}`,
                section: 'find',
                label: t('Find in {{label}}...', {label: t(option.label)}),
                group: option.group,
                keywords: option.keywords,
                searchTrigger: option.trigger,
                searchTerm: removeFindOptionTerm(term, option, t),
            }))
    }, [findOptions, hasCreateIntent, inputTrigger, normalizedSearchInputValue, searchInputValue, t])
    const results = React.useMemo(
        () => [...rankedStaticResults, ...providerResults],
        [providerResults, rankedStaticResults],
    )
    const recentResultIds = React.useMemo(
        () => new Set(recentResults.map(result => result.id)),
        [recentResults],
    )
    const visibleResults = React.useMemo(() => normalizedQuery
        ? results
        : results.filter(result => !recentResultIds.has(result.id)),
    [normalizedQuery, recentResultIds, results])
    const isIdle = !normalizedQuery
    const allGoToResults = visibleResults.filter(result => result.section === 'goTo')
    const allCreateResults = visibleResults.filter(result => result.section === 'create')
    const goToResults = isIdle && idleGoToItemIds
        ? idleGoToItemIds.flatMap(itemId => allGoToResults.filter(result => result.id === `goTo-${itemId}`))
        : allGoToResults
    const createResults = isIdle && idleCreateItemIds
        ? idleCreateItemIds.flatMap(itemId => allCreateResults.filter(result => result.id === `create-${itemId}`))
        : allCreateResults
    const findResults = isIdle ? [] : suggestedFindResults
    const searchResults = visibleResults.filter(result => result.section === 'result')
    const displayedRecentResults = isIdle ? recentResults : []
    const orderedResults = [
        ...displayedRecentResults,
        ...goToResults,
        ...findResults,
        ...searchResults,
        ...createResults,
    ]
    const selectedResultId = activeIndex === null ? undefined : orderedResults[activeIndex]?.id

    React.useEffect(() => {
        setActiveIndex(null)
        setActionError(false)
    }, [query])

    React.useEffect(() => {
        if (activeIndex === null || activeIndex < orderedResults.length) return
        setActiveIndex(orderedResults.length > 0 ? orderedResults.length - 1 : null)
    }, [activeIndex, orderedResults.length])

    React.useEffect(() => {
        if (!autoFocus) return
        const frame = window.requestAnimationFrame(() => inputRef.current?.select())
        return () => window.cancelAnimationFrame(frame)
    }, [autoFocus])

    React.useEffect(() => {
        if (!selectedResultId) return
        resultRefs.current[selectedResultId]?.scrollIntoView({block: 'nearest'})
    }, [selectedResultId])

    React.useEffect(() => {
        if (
            !normalizedQuery ||
            normalizedQuery.length < MIN_PROVIDER_QUERY_LENGTH ||
            !searchProvider ||
            !inputTrigger ||
            !hasSearchInputValue
        ) {
            setProviderResults([])
            setProvidersLoading(false)
            return
        }

        setProviderResults([])
        setProvidersLoading(true)
        let active = true
        const timeoutId = window.setTimeout(() => {
            Promise.resolve(searchProvider(query))
                .then(nextProviderResults => {
                    if (!active) return
                    setProviderResults(nextProviderResults)
                    setProvidersLoading(false)
                })
                .catch(() => {
                    if (!active) return
                    setProviderResults([])
                    setProvidersLoading(false)
                })
        }, PROVIDER_SEARCH_DEBOUNCE_MS)

        return () => {
            active = false
            window.clearTimeout(timeoutId)
        }
    }, [hasSearchInputValue, inputTrigger, normalizedQuery, query, searchProvider])

    const navigateToResult = (result: SettingsSearchResult) => {
        if (!result.path) return
        navigate(result.path)
        onClose?.()
    }

    const executeCommandAction = async (result: SettingsSearchResult) => {
        if (!result.onSelect || executingResultIdRef.current) return

        executingResultIdRef.current = result.id
        setExecutingResultId(result.id)
        setActionError(false)

        try {
            await result.onSelect()
            onClose?.()
        } catch {
            setActionError(true)
        } finally {
            executingResultIdRef.current = null
            setExecutingResultId(null)
        }
    }

    const activateFindResult = (result: SettingsSearchResult) => {
        if (!result.searchTrigger) return

        setQuery(`${result.searchTrigger}: ${result.searchTerm || ''}`)
        setActiveIndex(null)
        window.requestAnimationFrame(() => inputRef.current?.focus())
    }

    const selectResult = (result: SettingsSearchResult) => {
        if (result.section === 'find') {
            activateFindResult(result)
            return
        }

        if (result.onSelect) {
            void executeCommandAction(result)
            return
        }

        navigateToResult(result)
    }

    const handleSearchInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value
        setQuery(!inputTrigger || value.includes(':') ? value : `${inputTrigger.trigger}: ${value}`)
    }

    const removeInputTrigger = () => {
        setQuery(searchInputValue)
        window.requestAnimationFrame(() => inputRef.current?.focus())
    }

    const resetSearch = () => {
        setQuery('')
        setProviderResults([])
        setProvidersLoading(false)
        setActiveIndex(null)
        setActionError(false)
        window.requestAnimationFrame(() => inputRef.current?.focus())
    }

    const handleKeyDown = (event: React.KeyboardEvent) => {
        if (
            event.key === 'Backspace' &&
            inputTrigger &&
            inputRef.current?.selectionStart === 0 &&
            inputRef.current?.selectionEnd === 0
        ) {
            event.preventDefault()
            setQuery(inputTrigger.trigger)
            return
        }

        if (event.key === 'Tab' && !event.shiftKey && orderedResults.length > 0) {
            event.preventDefault()
            const firstResult = orderedResults[0]
            setActiveIndex(0)
            window.requestAnimationFrame(() => resultRefs.current[firstResult.id]?.focus())
            return
        }

        if (event.key === 'ArrowDown' && orderedResults.length > 0) {
            event.preventDefault()
            setActiveIndex(current => current === null ? 0 : (current + 1) % orderedResults.length)
            return
        }

        if (event.key === 'ArrowUp' && orderedResults.length > 0) {
            event.preventDefault()
            setActiveIndex(current => current === null
                ? orderedResults.length - 1
                : (current - 1 + orderedResults.length) % orderedResults.length)
            return
        }

        if (event.key !== 'Enter' || activeIndex === null || !orderedResults[activeIndex]) return

        event.preventDefault()
        selectResult(orderedResults[activeIndex])
    }

    const setActiveResult = (resultId: string) => {
        const nextActiveIndex = orderedResults.findIndex(result => result.id === resultId)
        if (nextActiveIndex >= 0) setActiveIndex(nextActiveIndex)
    }

    const handleResultKeyDown = (event: React.KeyboardEvent, resultId: string) => {
        const moveForward = event.key === 'ArrowDown' || (event.key === 'Tab' && !event.shiftKey)
        const moveBackward = event.key === 'ArrowUp' || (event.key === 'Tab' && event.shiftKey)
        if (!moveForward && !moveBackward) return

        const currentIndex = orderedResults.findIndex(result => result.id === resultId)
        if (currentIndex < 0) return

        event.preventDefault()
        const nextIndex = moveForward
            ? (currentIndex + 1) % orderedResults.length
            : (currentIndex - 1 + orderedResults.length) % orderedResults.length
        const nextResult = orderedResults[nextIndex]

        setActiveIndex(nextIndex)
        window.requestAnimationFrame(() => resultRefs.current[nextResult.id]?.focus())
    }

    return {
        actionError,
        activeSearchScopeLabel,
        autoFocus,
        canResetSearch: Boolean(query),
        createResults,
        displayedRecentResults,
        executingResultId,
        findResults,
        getResultDomId,
        goToResults,
        handleKeyDown,
        handleResultKeyDown,
        handleSearchInputChange,
        hasSearchInputValue,
        inputRef,
        inputTrigger,
        isIdle,
        orderedResults,
        providersLoading,
        removeInputTrigger,
        resetSearch,
        resultListboxId,
        resultRefs,
        searchInputValue,
        searchResults,
        selectResult,
        selectedResultId,
        setActiveResult,
    }
}

export type SettingsSearchController = ReturnType<typeof useSettingsSearch>
